import React, { useEffect, useRef, useState } from 'react';
import QrScanner from 'qr-scanner';

/**
 * QRScanner Component
 *
 * Camera-based QR code scanner using qr-scanner library
 */
const QRScanner = ({
  onScan,
  onError,
  isActive = false,
  className = ''
}) => {
  console.log('QRScanner component created/updated with props:', { isActive, onScan: !!onScan, onError: !!onError });

  const videoRef = useRef(null);
  const scannerRef = useRef(null);
  const [hasCamera, setHasCamera] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    console.log('QRScanner initial setup useEffect triggered');
    console.log('QrScanner import status:', { QrScanner, hasCamera: typeof QrScanner?.hasCamera });

    // Check if camera is available
    QrScanner.hasCamera().then(hasCamera => {
      setHasCamera(hasCamera);
      if (!hasCamera) {
        setError('No camera found on this device');
        setIsLoading(false);
      }
    }).catch(err => {
      console.error('Error checking camera availability:', err);
      setError('Unable to access camera features');
      setIsLoading(false);
    });
  }, []);

  // Mobile visibility handler ref
  const visibilityHandlerRef = useRef(null);

  // Effect to start scanner when video element becomes available
  useEffect(() => {
    console.log('QRScanner main useEffect triggered:', {
      isActive,
      videoReady: !!videoRef.current,
      hasCamera,
      isLoading,
      permissionGranted
    });

    // Only proceed if active, video element exists, camera is available, and scanner not already running
    if (!isActive || !videoRef.current || !hasCamera || scannerRef.current) {
      console.log('QRScanner conditions not met:', {
        isActive,
        videoElement: !!videoRef.current,
        hasCamera,
        scannerExists: !!scannerRef.current
      });
      return;
    }

    console.log('QRScanner conditions met, proceeding to start scanner');

    const startScanner = async () => {
      try {
        console.log('Starting QR scanner...');
        console.log('Protocol:', window.location.protocol);
        console.log('Host:', window.location.host);

        // Create QR scanner instance
        const scanner = new QrScanner(
          videoRef.current,
          (result) => {
            console.log('QR Code scanned:', result.data);
            if (onScan) {
              onScan(result.data);
            }
          },
          {
            returnDetailedScanResult: true,
            highlightScanRegion: false, // Disable built-in highlighting - we use custom overlay
            highlightCodeOutline: false, // Disable built-in highlighting - we use custom overlay
            preferredCamera: 'environment', // Use back camera on mobile
            maxScansPerSecond: 5, // Reduce scan frequency to help mobile performance
            onDecodeError: (error) => {
              // Only log decode errors in debug mode, don't show to user
              console.log('QR decode error (normal):', error);
            }
          }
        );

        scannerRef.current = scanner;

        // Start scanning - this will prompt for camera permission
        console.log('Requesting camera access...');
        await scanner.start();
        console.log('Camera access granted, scanner started');

        // Mobile-specific: Add visibility change listener to restart camera
        const handleVisibilityChange = () => {
          if (document.hidden) {
            console.log('Page hidden - pausing scanner');
          } else {
            console.log('Page visible - ensuring scanner is active');
            if (scannerRef.current && !scannerRef.current._active) {
              console.log('Restarting scanner after visibility change');
              scannerRef.current.start().catch(err => {
                console.error('Failed to restart scanner:', err);
              });
            }
          }
        };

        visibilityHandlerRef.current = handleVisibilityChange;
        document.addEventListener('visibilitychange', handleVisibilityChange);

        setPermissionGranted(true);
        setIsLoading(false);

      } catch (err) {
        console.error('Error starting QR scanner:', err);
        console.error('Error details:', {
          name: err.name,
          message: err.message,
          stack: err.stack
        });

        let errorMessage = 'Unable to start camera. ';

        if (err.name === 'NotAllowedError') {
          errorMessage = 'Camera access denied. Please allow camera permissions in your browser and try again.';
          console.log('Camera permission denied by user');
        } else if (err.name === 'NotFoundError') {
          errorMessage = 'No camera found on this device.';
        } else if (err.name === 'NotSupportedError') {
          errorMessage = 'Camera not supported in this browser.';
        } else if (err.name === 'NotReadableError') {
          errorMessage = 'Camera is already in use by another application.';
        } else if (err.name === 'OverconstrainedError') {
          errorMessage = 'Camera constraints cannot be satisfied.';
        } else if (err.name === 'SecurityError') {
          errorMessage = 'Camera access blocked for security reasons. This might be because the page is not served over HTTPS.';
        } else {
          errorMessage += `Error: ${err.message}`;
        }

        setError(errorMessage);
        setIsLoading(false);
        if (onError) {
          onError(err);
        }
      }
    };

    // Small delay to ensure video element is fully rendered
    const timer = setTimeout(startScanner, 100);

    // Cleanup function
    return () => {
      clearTimeout(timer);
      if (scannerRef.current) {
        scannerRef.current.stop();
        scannerRef.current.destroy();
        scannerRef.current = null;
      }
      // Remove visibility change listener
      if (visibilityHandlerRef.current) {
        document.removeEventListener('visibilitychange', visibilityHandlerRef.current);
        visibilityHandlerRef.current = null;
      }
    };
  }, [isActive, hasCamera, onScan, onError]);

  // Stop scanner when component becomes inactive
  useEffect(() => {
    if (!isActive && scannerRef.current) {
      scannerRef.current.stop();
    } else if (isActive && scannerRef.current && permissionGranted) {
      scannerRef.current.start().catch(err => {
        console.error('Error restarting scanner:', err);
      });
    }
  }, [isActive, permissionGranted]);

  return (
    <div className={`relative ${className}`}>
      {/* Always render video element for ref access */}
      <video
        ref={videoRef}
        className="w-full max-w-xs mx-auto rounded-lg border-2 border-gray-300"
        style={{ aspectRatio: '1' }}
        muted
        playsInline
        autoPlay
        webkit-playsinline="true"
      />

      {/* Overlay states on top of video */}
      {!hasCamera && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 bg-opacity-90 rounded-lg">
          <div className="text-center p-4">
            <div className="text-gray-400 text-4xl mb-2">📷</div>
            <p className="text-sm text-gray-600">No camera available</p>
            <p className="text-xs text-gray-500 mt-1">Use manual entry instead</p>
          </div>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-red-50 bg-opacity-90 rounded-lg">
          <div className="text-center p-4">
            <div className="text-red-400 text-4xl mb-2">⚠️</div>
            <p className="text-sm text-red-600 mb-2">{error}</p>
            <p className="text-xs text-red-500">Use manual entry instead</p>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-blue-50 bg-opacity-90 rounded-lg">
          <div className="text-center p-4">
            {console.log('QRScanner rendering loading overlay:', { isLoading, hasCamera, permissionGranted, isActive })}
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
            <p className="text-sm text-blue-600">Starting camera...</p>
            <p className="text-xs text-blue-500 mt-1">Please allow access when prompted</p>
          </div>
        </div>
      )}

      {/* Scanning Instructions - only show when camera is active */}
      {permissionGranted && !error && (
        <div className="text-center mt-2">
          <p className="text-sm text-gray-600">Point camera at QR code</p>
          <p className="text-xs text-gray-500">Scanning will happen automatically</p>
        </div>
      )}

      {/* Scanning Frame Overlay - only show when camera is active */}
      {permissionGranted && !error && !isLoading && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 border-2 border-green-500 rounded-lg opacity-70">
            <div className="absolute top-0 left-0 w-4 h-4 border-t-4 border-l-4 border-green-500"></div>
            <div className="absolute top-0 right-0 w-4 h-4 border-t-4 border-r-4 border-green-500"></div>
            <div className="absolute bottom-0 left-0 w-4 h-4 border-b-4 border-l-4 border-green-500"></div>
            <div className="absolute bottom-0 right-0 w-4 h-4 border-b-4 border-r-4 border-green-500"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRScanner;